/**
 * Mad Dad Labz Analytics Integration
 * 
 * Handles GA4 event tracking for homepage elevation components.
 * Integrates with existing CustomEvent system and provides
 * centralized analytics configuration.
 */

(function() {
  'use strict';
  
  // Configuration from theme settings
  const CONFIG = {
    enabled: window.madDadAnalytics?.enabled || false,
    measurementId: window.madDadAnalytics?.measurementId || null,
    debugMode: window.madDadAnalytics?.debugMode || false
  };
  
  // Event mappings for homepage elevation
  const EVENT_MAPPINGS = {
    'homepage_cta_click': {
      ga4Event: 'homepage_cta_click',
      parameters: ['button_type', 'destination', 'section_id']
    },
    'tab_switch_product_range': {
      ga4Event: 'tab_switch_product_range', 
      parameters: ['tab_handle', 'tab_label', 'section_id']
    },
    'use_case_tile_click': {
      ga4Event: 'use_case_tile_click',
      parameters: ['tile_type', 'destination', 'section_id']
    },
    'cta_band_click': {
      ga4Event: 'cta_band_click',
      parameters: ['button_type', 'destination', 'section_id']
    },
    'survey_response_clarity': {
      ga4Event: 'survey_response_clarity',
      parameters: ['response', 'question', 'timestamp']
    }
  };
  
  // Initialize analytics system
  function init() {
    if (!CONFIG.enabled) {
      log('Analytics disabled in theme settings');
      return;
    }
    
    // Setup GA4 if measurement ID is provided
    if (CONFIG.measurementId && !window.gtag) {
      setupGA4();
    }
    
    // Bind event listeners for homepage components
    bindEventListeners();
    
    // Expose global functions for manual tracking
    exposeGlobalAPI();
    
    log('Mad Dad Labz analytics initialized');
  }
  
  // Setup Google Analytics 4
  function setupGA4() {
    // Create gtag function if it doesn't exist
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      dataLayer.push(arguments);
    };
    
    // Initialize GA4
    gtag('js', new Date());
    gtag('config', CONFIG.measurementId, {
      debug_mode: CONFIG.debugMode,
      custom_map: {
        'custom_parameter': 'custom_parameter'
      }
    });
    
    log(`GA4 initialized with measurement ID: ${CONFIG.measurementId}`);
  }
  
  // Bind event listeners for CustomEvents
  function bindEventListeners() {
    // Product range tabs
    document.addEventListener('tab_switch_product_range', function(event) {
      trackEvent('tab_switch_product_range', {
        tab_handle: event.detail.tabHandle,
        tab_label: event.detail.label,
        section_id: event.detail.sectionId
      });
    });
    
    // Microsurvey responses
    document.addEventListener('survey_response_clarity', function(event) {
      trackEvent('survey_response_clarity', {
        response: event.detail.response,
        question: event.detail.question,
        timestamp: event.detail.timestamp
      });
    });
    
    // Use case reactor tiles (will be added when section is implemented)
    document.addEventListener('use_case_tile_click', function(event) {
      trackEvent('use_case_tile_click', {
        tile_type: event.detail.tileType,
        destination: event.detail.destination,
        section_id: event.detail.sectionId
      });
    });
    
    // CTA band clicks
    document.addEventListener('cta_band_click', function(event) {
      trackEvent('cta_band_click', {
        button_type: event.detail.buttonType,
        destination: event.detail.destination,
        section_id: event.detail.sectionId
      });
    });
    
    // Hero CTA clicks
    document.addEventListener('homepage_cta_click', function(event) {
      trackEvent('homepage_cta_click', {
        button_type: event.detail.buttonType,
        destination: event.detail.destination,
        section_id: event.detail.sectionId
      });
    });
  }
  
  // Track an event
  function trackEvent(eventType, parameters = {}) {
    if (!CONFIG.enabled) {
      log(`Analytics disabled, skipping event: ${eventType}`);
      return;
    }
    
    const mapping = EVENT_MAPPINGS[eventType];
    if (!mapping) {
      log(`No mapping found for event: ${eventType}`, 'warn');
      return;
    }
    
    // Filter parameters to only include expected ones
    const filteredParams = {};
    mapping.parameters.forEach(param => {
      if (parameters[param] !== undefined) {
        filteredParams[param] = parameters[param];
      }
    });
    
    // Add common parameters
    filteredParams.page_template = window.Shopify?.template || 'unknown';
    filteredParams.timestamp = new Date().toISOString();
    
    // Track with GA4 if available
    if (window.gtag && CONFIG.measurementId) {
      gtag('event', mapping.ga4Event, filteredParams);
      log(`GA4 event tracked: ${mapping.ga4Event}`, filteredParams);
    }

    const shopifyTrack = window.Shopify?.analytics?.track;
    if (typeof shopifyTrack === 'function') {
      try {
        shopifyTrack.call(Shopify.analytics, mapping.ga4Event, filteredParams);
        log(`Shopify analytics event tracked: ${mapping.ga4Event}`, filteredParams);
      } catch (error) {
        log(`Shopify analytics error: ${error.message}`, 'error');
      }
    } else if (window.Shopify?.analytics) {
      log('Shopify analytics track unavailable, storing event for later dispatch', 'warn', {
        event: mapping.ga4Event,
        parameters: filteredParams
      });
      queueDeferredShopifyEvent(mapping.ga4Event, filteredParams);
    }
    
    // Dispatch custom event for debugging
    document.dispatchEvent(new CustomEvent('mad_dad_analytics_tracked', {
      detail: {
        eventType: mapping.ga4Event,
        parameters: filteredParams
      }
    }));
  }
  
  const deferredShopifyEvents = [];

  function queueDeferredShopifyEvent(eventName, params) {
    deferredShopifyEvents.push({ eventName, params });
  }

  function flushDeferredShopifyEvents() {
    if (!deferredShopifyEvents.length) return;

    const shopifyTrack = window.Shopify?.analytics?.track;
    if (typeof shopifyTrack !== 'function') {
      return;
    }

    deferredShopifyEvents.splice(0).forEach(({ eventName, params }) => {
      try {
        shopifyTrack.call(Shopify.analytics, eventName, params);
        log(`Deferred Shopify analytics event tracked: ${eventName}`, params);
      } catch (error) {
        log(`Deferred Shopify analytics error: ${error.message}`, 'error');
      }
    });
  }

  document.addEventListener('shopify:analytics:ready', flushDeferredShopifyEvents, { once: true });

  // Expose global API for manual tracking
  function exposeGlobalAPI() {
    window.MadDadAnalytics = {
      track: trackEvent,
      trackCTAClick: function(buttonType, destination, sectionId) {
        trackEvent('homepage_cta_click', {
          button_type: buttonType,
          destination: destination,
          section_id: sectionId
        });
      },
      trackTabSwitch: function(tabHandle, tabLabel, sectionId) {
        trackEvent('tab_switch_product_range', {
          tab_handle: tabHandle,
          tab_label: tabLabel,
          section_id: sectionId
        });
      },
      trackSurveyResponse: function(response, question) {
        trackEvent('survey_response_clarity', {
          response: response,
          question: question,
          timestamp: new Date().toISOString()
        });
      },
      getConfig: function() {
        return { ...CONFIG };
      }
    };
  }
  
  // Logging utility
  function log(message, level = 'info', data = null) {
    if (!CONFIG.debugMode && level !== 'error') return;
    
    const prefix = '[Mad Dad Analytics]';
    
    switch (level) {
      case 'error':
        console.error(prefix, message, data);
        break;
      case 'warn':
        console.warn(prefix, message, data);
        break;
      default:
        console.log(prefix, message, data);
    }
  }
  
  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
})();
